import {tokens} from '@pleo-io/telescope'
import {useAnimation} from 'framer-motion'
import React from 'react'
import {useInView} from 'react-intersection-observer'
import styled, {css} from 'styled-components'

import type {RefType as CarouselRefType} from '@/components/carousel'
import Carousel from '@/components/carousel'
import {AspectRatio} from '@/components/inline-wistia-video'
import PlayButton from '@/components/play-button'
import PopoverVideo from '@/components/popover-video'
import Image from '@helios/shared/components/image'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {gutters, Section} from '@helios/shared/styles/grid'
import {Text, text} from '@helios/shared/styles/typography'
import type ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'

import * as tracking from '../tracking'
import SvgOutline from './svg-outline'

const smallBreakpoint = 'md'

interface Tab {
    description: string
    name_title: string
    video_id: string
    thumbnail: ImageSource
    customer_logo: ImageSource
}

interface Props {
    data: {
        section_title: string
        video_list: Tab[]
    }
}

export const VideoWithAnimatedOutline = ({data}: Props) => {
    const isSmallScreen = useMediaQuery.max(smallBreakpoint)
    const globalStrings = useGlobalStrings()

    const [active, setActive] = React.useState(0)
    const wrapperRef = React.useRef<HTMLDivElement>(null)
    const sliderRef = React.useRef<CarouselRefType>(null)
    const {trackFirstInteraction} = tracking.useHomepageTracking(wrapperRef, 'testimonials_section')

    const dots = data.video_list.map(({video_id}, i) => (
        <CarouselDot
            key={`${video_id}-${i}`}
            $isActive={active === i}
            onClick={() => {
                trackFirstInteraction('testimonials_interacted')
                sliderRef.current?.slideGoTo(i)
            }}
            aria-label={`${globalStrings.panel} ${i + 1}`}
        />
    ))

    return (
        <Section ref={wrapperRef} edgeToEdge={isSmallScreen}>
            <Wrapper>
                <TitleWrapper>
                    <SectionTitle as="h2" variant="heading-base">
                        <TextWithAnimatedValues text={data.section_title} />
                    </SectionTitle>
                </TitleWrapper>

                <VideoList>
                    <StyledCarousel
                        onSlideChange={(index: number) => {
                            setActive(index)
                            trackFirstInteraction('testimonials_interacted')
                        }}
                        ref={sliderRef}
                    >
                        {data.video_list.map((video) => (
                            <VideoThumbnail
                                key={video.video_id}
                                video={video}
                                onVideoPlay={() => trackFirstInteraction('testimonials_interacted')}
                            />
                        ))}
                    </StyledCarousel>

                    {isSmallScreen && <CarouselDots>{dots}</CarouselDots>}
                </VideoList>
            </Wrapper>
        </Section>
    )
}

interface VideoThumbnailProps {
    video: {
        description: string
        name_title: string
        video_id: string
        thumbnail: ImageSource
        customer_logo: ImageSource
    }
    onVideoPlay: () => void
}

const VideoThumbnail = ({video, onVideoPlay}: VideoThumbnailProps) => {
    const {play} = useGlobalStrings()

    return (
        <VideoThumbnailWrapper>
            <PopoverVideo videoId={video.video_id} aspectRatio={AspectRatio.RatioCinemascope}>
                {({openPopover}) => (
                    <ImageWrapper>
                        {video.thumbnail && (
                            <Image
                                image={video.thumbnail}
                                style={{position: 'absolute', inset: 0}}
                            />
                        )}

                        <VideoPlayButton
                            size="small"
                            onClick={() => {
                                onVideoPlay()
                                openPopover()
                            }}
                            aria-label={play}
                        />
                    </ImageWrapper>
                )}
            </PopoverVideo>

            <LogoSpacer>
                <Logo image={video.customer_logo} />
            </LogoSpacer>

            <Testimonial variant="paragraph-large">{video.description}</Testimonial>

            <NameAndTitle variant="label-base">{video.name_title}</NameAndTitle>
        </VideoThumbnailWrapper>
    )
}

interface AnimatedSvgProps {
    text: string
}

const AnimatedSvg = ({text}: AnimatedSvgProps) => {
    const animationControls = useAnimation()
    const [svgWrapperRef, inView] = useInView({
        threshold: 0.5,
        triggerOnce: true,
    })

    React.useEffect(() => {
        if (!inView) {
            return
        }

        animationControls.start('visible')

        return () => {
            animationControls.stop()
        }
    }, [animationControls, inView])

    return (
        <SvgWrapper ref={svgWrapperRef}>
            <SvgOutline animationControls={animationControls} />
            {text}
        </SvgWrapper>
    )
}

const TextWithAnimatedValues = ({text}: {text: string}) => {
    const regex = new RegExp(/({{.*}})/, 'gi')
    const parts = text.split(regex)
    const isSmallScreen = useMediaQuery.max(smallBreakpoint)
    const element = parts.map((part, i) => {
        if (regex.test(part)) {
            return isSmallScreen ? (
                part.replace(/{|}/g, '')
            ) : (
                <AnimatedSvg key={`part-${i}`} text={part.replace(/{|}/g, '\xa0\xa0')} />
            )
        }
        return part
    })
    return <>{element}</>
}

const Wrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
    justify-content: center;
    grid-template-columns: auto;

    ${mq.min('sm')} {
        gap: ${tokens.spacing60};
    }
`

const TitleWrapper = styled.div`
    ${mq.max('sm')} {
        ${gutters}
    }
`

const SectionTitle = styled(Text)`
    color: ${tokens.shade900};
    ${text({variant: 'heading-base'})}

    ${mq.min('sm')} {
        text-align: center;
    }
`

const StyledCarousel = styled(Carousel)`
    ${mq.min(smallBreakpoint)} {
        gap: 70px;
        grid-template-columns: repeat(3, 1fr);
    }
`

const VideoList = styled.div`
    display: grid;
    row-gap: ${tokens.spacing32};
`

const ImageWrapper = styled.div`
    position: relative;
    height: 0;
    padding-top: 50%;
`

const VideoPlayButton = styled(PlayButton)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`

const VideoThumbnailWrapper = styled.div`
    ${mq.max(smallBreakpoint)} {
        ${gutters}
    }
`

const Logo = styled(Image)`
    max-width: 80px;
    height: 40px;
    object-fit: contain;

    ${mq.min('sm')} {
        padding-left: 0;
    }
`

const LogoSpacer = styled.div`
    padding-top: ${tokens.spacing40};
    padding-bottom: ${tokens.spacing4};
`

const Testimonial = styled(Text)`
    padding-bottom: ${tokens.spacing10};
    color: ${tokens.shade900};
`

const NameAndTitle = styled(Text)`
    color: ${tokens.shade600};
`

const SvgWrapper = styled.span`
    position: relative;
`

export const CarouselDots = styled.nav`
    display: grid;
    grid-auto-flow: column;
    gap: ${tokens.spacing20};
    align-items: center;
    justify-content: center;
`

interface CarouselDotProps {
    $isActive?: boolean
}

export const CarouselDot = styled.button<CarouselDotProps>(({$isActive}) => {
    const isActiveStyles = css`
        width: 8px;
        height: 8px;
        background: ${tokens.shade600};
    `

    return css`
        width: 6px;
        height: 6px;
        padding: 0;
        cursor: pointer;
        background: ${tokens.shade400};
        border: none;
        border-radius: ${tokens.circle};

        ${$isActive && isActiveStyles}
    `
})
